'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import CorrectionNote from '@/app/components/blog/CorrectionNote'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Berkey Filter Microplastics? Berkey Hasn't Tested It (2026)"
      author="MicroPlastics Research Desk"
      date="July 15, 2026"
      readTime="9 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Does Berkey filter microplastics: the gravity-fed Black Berkey system and what has and has not been tested"
    >
      <BlogLastUpdated iso="2026-09-21" formatted="September 21, 2026" />

      <CorrectionNote date="September 21, 2026">
        This article previously said Berkey&apos;s own lab testing shows 99.9%+ microplastic reduction and that removal was &ldquo;almost certainly&rdquo; happening. Berkey says it has not tested its elements for plastic particles and cannot make that claim, and it publishes no pore size. We also said Berkey holds no certification of any kind (its Phoenix elements are NSF 42 certified for chlorine, taste and odour), called AquaTru &ldquo;P473&rdquo; certified (it is not), and did not mention EPA&apos;s 2022 and 2023 stop-sale orders on Black Berkey products.
      </CorrectionNote>

      <BlogTLDR>
        <strong>Nobody knows, including Berkey.</strong> Berkey says actual testing
        of plastic particles &ldquo;has not yet been conducted&rdquo;, so it cannot
        claim microplastic removal, and it publishes no pore size for its Black
        Berkey elements. The elements hold no certification from NSF, WQA or IAPMO.
        In 2022 and 2023 EPA ordered sales of Black Berkey products stopped, over
        antimicrobial claims made without pesticide registration. If you want
        gravity-fed water with certified microplastic reduction, the{' '}
        <strong>PUR PLUS</strong>, <strong>Brita Elite</strong> and{' '}
        <strong>LifeStraw Home glass</strong> pitchers are certified to NSF/ANSI 401
        and need no power either.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Berkey says it has <strong>not tested</strong> its elements for plastic particles and cannot claim microplastic removal.</>,
          <>Berkey calls the Black Berkey elements &ldquo;a proprietary blend of media&rdquo; and publishes no micron rating.</>,
          <>The Black Berkey elements have <strong>no certification</strong> from NSF, WQA or IAPMO. Berkey&apos;s replacement Phoenix elements are NSF 42 certified for chlorine, taste and odour only.</>,
          <>EPA issued stop-sale orders on Black Berkey products in December 2022 (a dealer) and May 2023 (the manufacturer), because they were sold with virus- and bacteria-killing claims but not registered as pesticides.</>,
          <>Gravity-fed pitchers certified for microplastics exist: PUR PLUS (99.8%), Brita Elite (99.6%), LifeStraw Home glass (at least 99%).</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_berkey" />

      <KeyStats
        heading="Berkey and microplastics: what the record shows"
        stats={[
          { value: 'Not tested', label: 'for plastic particles', context: 'Berkey says it cannot officially claim microplastic removal', source: 'Berkey knowledge base', sourceUrl: 'https://berkeywaterkb.com/do-the-black-berkey-purification-elements-remove-plastic-fibers-from-the-water/' },
          { value: 'None', label: 'certification for Black Berkey', context: 'no NSF, WQA or IAPMO listing for the elements', source: 'Berkey knowledge base', sourceUrl: 'https://berkeywaterkb.com/are-the-black-berkey-purification-elements-nsf-certified/' },
          { value: 'NSF 42', label: 'Phoenix replacement elements', context: 'chlorine, taste and odour only; no microplastics claim', source: 'Berkey', sourceUrl: 'https://www.berkeywater.com/phoenix-gravity-new-millennium-edition-filter-elements/' },
          { value: '2022–23', label: 'EPA stop-sale orders', context: 'unregistered antimicrobial (pesticide) claims, not filtration performance', source: 'US EPA Region 8', sourceUrl: 'https://yosemite.epa.gov/oa/rhc/epaadmin.nsf/Filings/A56B5681D18012EF85258925007F0A58/$File/FIFRA-08-2023-0011%20BerkeyFilters%2012-2022.pdf' },
        ]}
      />

      <h2>Does Berkey remove microplastics?</h2>
      <p>
        There is no evidence either way. On its own support pages, Berkey says that
        because &ldquo;actual testing of plastic fibers has not yet been
        conducted&rdquo;, it &ldquo;can&apos;t officially make that claim.&rdquo; We
        found no peer-reviewed study of Berkey and microplastics, and Berkey
        publishes no pore size for the Black Berkey elements, so there is no basis
        for estimating how small a particle they stop.
      </p>
      <p>
        Berkey does publish its own test reports for other contaminants, including
        PFOA and heavy metals, and describes the elements as able to reduce
        contaminants in a tested range of 24&ndash;26 nanometres. Those reports are
        commissioned and published by the brand, and none of them is a
        microplastics test.
      </p>

      <h2>What the elements are</h2>
      <p>
        Berkey describes the Black Berkey elements as &ldquo;a proprietary blend of
        media&rdquo;. A dealer&apos;s description quoted in EPA&apos;s order calls them
        a carbon composite of coconut-shell carbon and five other media. They are
        not ceramic. A pair is rated for 6,000 gallons. Water runs through them by
        gravity, with no power or plumbing, which is why Berkey is popular for
        cabins, travel and emergency kits.
      </p>

      <h2>Certification</h2>
      <p>
        The Black Berkey elements are not certified by NSF, the WQA or IAPMO.
        Berkey&apos;s position is that NSF certification is optional and poorly
        suited to gravity-fed elements. Berkey now also sells{' '}
        <strong>Phoenix Gravity New Millennium Edition</strong> elements
        &ldquo;in lieu of Black Berkey&rdquo;, and NSF lists the Big, Royal and
        Travel Berkey fitted with them under NSF/ANSI 42, for chlorine, taste and
        odour. That is not a microplastics certification: only NSF/ANSI 401 has
        one.
      </p>

      <h2>The EPA orders</h2>
      <p>
        In December 2022, EPA Region 8 ordered James Enterprise, a dealer trading
        as &ldquo;Berkey Filters&rdquo;, to stop selling Black Berkey products. In May
        2023 it issued the same order to Berkey International, the manufacturer.
        The reason was the marketing, not the filtration: the products were sold
        with claims to kill viruses and bacteria, which makes them pesticides that
        must be registered with EPA, and they were not. New Millennium Concepts,
        which sells Berkey, sued; the case was dismissed and the Fifth Circuit upheld the dismissal in
        August 2024. The orders stay in force until EPA revokes them, and we found
        no record that it has. Berkey&apos;s site lists the Big Berkey as limited
        supply and does not sell to residents of California or Iowa.
      </p>

      <h2>Berkey vs the certified alternatives</h2>
      <BlogComparisonTable
        caption="Berkey vs filters with a certified microplastics claim"
        columns={[
          { key: 'filter', label: 'Filter' },
          { key: 'format', label: 'Format' },
          { key: 'proof', label: 'Microplastics evidence' },
        ]}
        rows={[
          { filter: 'Berkey (Black Berkey elements)', format: 'Gravity-fed, no power', proof: 'Not tested, per Berkey; no certification' },
          { filter: 'PUR PLUS', format: 'Gravity pitcher, no power', proof: 'NSF/ANSI 401: 99.8% (IAPMO)' },
          { filter: 'Brita Elite', format: 'Gravity pitcher, no power', proof: 'NSF/ANSI 401: 99.6% (WQA)' },
          { filter: 'LifeStraw Home glass', format: 'Gravity pitcher, no power', proof: 'NSF/ANSI 401: at least 99% (WQA)' },
          { filter: 'AquaTru Classic', format: 'Countertop reverse osmosis, needs power', proof: 'NSF/ANSI 401: 96.48% (IAPMO)' },
          { filter: 'Clearly Filtered', format: 'Gravity pitcher, no power', proof: 'Brand lab tests; not certified for microplastics' },
        ]}
      />
      <p>
        The full comparison, with prices and cost per litre, is in{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-forest underline">
          best water filter for microplastics
        </Link>
        . More on the alternatives:{' '}
        <Link href="/blog/does-aquatru-remove-microplastics" className="text-forest underline">AquaTru</Link>{' '}
        and{' '}
        <Link href="/blog/does-clearly-filtered-remove-microplastics" className="text-forest underline">Clearly Filtered</Link>.
        Whatever you filter with, pour into a{' '}
        <Link href="/blog/best-glass-water-bottles" className="text-forest underline">glass or steel bottle</Link>{' '}
        so you are not adding plastic back afterwards.
      </p>

      <BlogAppCTA
        headline="Your filter is one piece of the puzzle"
        sub="A filter handles your drinking water. Scan the bottled water, food packaging, and kitchen gear around it with the MicroPlastics app to see where plastic sneaks back in."
      />

      <BlogFAQ
        items={[
          {
            question: 'Does Berkey remove microplastics?',
            answer: 'Unknown. Berkey says actual testing of plastic particles has not been conducted, so it cannot claim microplastic removal, and it publishes no pore size for the Black Berkey elements. No peer-reviewed study has tested it.',
          },
          {
            question: 'Is Berkey NSF certified?',
            answer: 'The Black Berkey elements are not certified by NSF, WQA or IAPMO. NSF does list the Big, Royal and Travel Berkey fitted with Berkey’s Phoenix replacement elements under NSF/ANSI 42, for chlorine, taste and odour only.',
          },
          {
            question: 'Why did EPA stop Berkey sales?',
            answer: 'EPA issued stop-sale orders in December 2022 and May 2023 because Black Berkey products were marketed as killing viruses and bacteria, which requires pesticide registration, and they were not registered. The orders were about those claims, not about filtration performance.',
          },
          {
            question: 'What is a gravity filter certified for microplastics?',
            answer: 'Pitchers are gravity filters too. The PUR PLUS (99.8%), Brita Elite (99.6%) and LifeStraw Home glass (at least 99%) are certified to NSF/ANSI 401 for microplastics and need no power or plumbing.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'New Millennium Concepts (Berkey)', year: 2023, title: 'Do the Black Berkey elements remove plastic fibers from the water?', publisher: 'Berkey knowledge base', url: 'https://berkeywaterkb.com/do-the-black-berkey-purification-elements-remove-plastic-fibers-from-the-water/' },
          { authors: 'New Millennium Concepts (Berkey)', year: 2026, title: 'Are the Black Berkey elements NSF certified?', publisher: 'Berkey knowledge base', url: 'https://berkeywaterkb.com/are-the-black-berkey-purification-elements-nsf-certified/' },
          { authors: 'New Millennium Concepts (Berkey)', year: 2026, title: 'Phoenix Gravity New Millennium Edition filter elements', publisher: 'berkeywater.com', url: 'https://www.berkeywater.com/phoenix-gravity-new-millennium-edition-filter-elements/' },
          { authors: 'US Environmental Protection Agency, Region 8', year: 2022, title: 'Stop Sale, Use or Removal Order FIFRA-08-2023-0011 (James Enterprise, dba Berkey Filters)', publisher: 'EPA', url: 'https://yosemite.epa.gov/oa/rhc/epaadmin.nsf/Filings/A56B5681D18012EF85258925007F0A58/$File/FIFRA-08-2023-0011%20BerkeyFilters%2012-2022.pdf' },
          { authors: 'US Environmental Protection Agency, Region 8', year: 2023, title: 'Stop Sale, Use or Removal Order FIFRA-08-2023-0038 (Berkey International)', publisher: 'EPA', url: 'https://yosemite.epa.gov/OA/RHC/EPAAdmin.nsf/Filings/70BF71F4C6BC372C852589AA0000E556/$File/FIFRA-08-2023-0038%20SSURO%20BerkeyInternational%202023.pdf' },
          { authors: 'US Court of Appeals for the Fifth Circuit', year: 2024, title: 'Opinion in No. 23-11189, affirming dismissal of the Berkey maker\'s suit against EPA', publisher: 'ca5.uscourts.gov', url: 'https://www.ca5.uscourts.gov/opinions/unpub/23/23-11189.0.pdf' },
        ]}
      />
    </BlogPostLayout>
  )
}
